import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { DefaultTheme } from "styled-components/native";
import { Input, VDBText } from "@components/atoms";
import { ActivityIndicator, FlatList, Platform, View } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { SelectionType } from "@components/atoms/Filters/types";
import {
  NavigationProp,
  useFocusEffect,
  useNavigation,
} from "@react-navigation/native";
import { useSelector } from "react-redux";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchGroupSlice } from "@redux/slices/searchGroupsSlice";
import { Controller, useForm } from "react-hook-form";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { VDBIcons } from "@utils/VDBIcons";
import { Portal } from "@gorhom/portal";
import { TextInput } from "react-native-gesture-handler";
import RadioButton from "../RadioButton";
import { useSearchFormData } from "@apis/dynamicFormApi";
import { useDebounce } from "@components/molecules/DynamicForm/utils";
import { getHeaderStyleBasedOnProduct } from "@components/common/Header/utils";
import { ProductCategory } from "@utils/constants";
import Routes from "@navigations/Routes";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";

const DropDownSearchScreen = (props: any) => {
  const { route } = props;
  const params = route && route.params ? route.params : props;
  const {
    title: label,
    customIcon,
    selectionType,
    api,
    server_side_search,
    selectedStyle,
    api_query_key,
    name,
    inputLabel,
    shareType,
    onSelectItem,
    dynamicFormValues,
  } = params;
  const selectedTheme: SelectedItemTheme = selectedStyle;
  const { bottom, top } = useSafeAreaInsets();
  const navigation = useNavigation<NavigationProp<any>>();
  const navigationHeight = useSelector(
    (state: any) => state.appState?.navigationHeight ?? 0
  );
  const headerOverlaysContent =
    navigation.getState()?.routeNames?.includes(Routes.OrderCartScreen) ??
    false;
  const topOffset = headerOverlaysContent
    ? navigationHeight > 0
      ? navigationHeight
      : top + 56
    : 0;
  const productType = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const { theme, t, typography } = useScreenOptionsHooks();
  const [options, setOptions] = useState<any[] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState(options);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [startRangeIndex, setStartRangeIndex] = useState<number | null>(null);
  const { selectedGroups } = useSearchGroupSlice();
  const queryClient = useQueryClient();
  const inputRef = useRef<TextInput>(null);

  const { textColor } = getHeaderStyleBasedOnProduct(
    ProductCategory.DEFAULT,
    theme
  );
  const headerTitleColor = theme[textColor as keyof DefaultTheme] as string;

  let mGroupIds = selectedGroups[productType] ?? [];
  if (mGroupIds.includes(0)) {
    mGroupIds = mGroupIds.filter((groupId: number) => groupId !== 0);
  }

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { data, isFetching } = useSearchFormData(
    !!debouncedSearchQuery && !!api,
    api,
    debouncedSearchQuery,
    api_query_key,
    shareType
  );

  const { control, reset } = useForm({
    defaultValues: {
      searchQueryFormValue: "",
    },
  });

  useFocusEffect(
    useCallback(() => {
      return () => {
        queryClient.removeQueries({ queryKey: ["GET_SEARCH_FILTERS_PAYLOAD"] });
      };
    }, [queryClient])
  );

  useEffect(() => {
    if (api) {
      setFilteredData(data?.response?.body);
    }
  }, [data?.response?.body]);

  useEffect(() => {
    navigation.setOptions({
      headerTitle: t(label),
      headerTintColor: headerTitleColor,
      headerTitleStyle: {
        ...typography["H2-M"],
        color: headerTitleColor,
      },
    });
  }, []);

  useEffect(() => {
    if (server_side_search) {
      setOptions(data?.data || data);
    } else {
      setOptions(data?.data?.list || data?.data || data);
    }
  }, [data]);

  const resetAllFilters = () => {
    setSelectedItems([]);
    reset();
    setSearchQuery("");
  };

  const updateDynamicFormValues = (
    prev: any,
    fieldName: string,
    value: string
  ) => {
    return {
      ...prev,
      value: value,
      requestForm: {
        ...prev.requestForm,
        [fieldName]: value,
      },
    };
  };

  const applyFilters = () => {
    if (selectedItems.length !== 0 || searchQuery !== "") {
      if (selectedItems.length !== 0) {
        onSelectItem(selectedItems[0]);
      } else {
        let updatedDynamicValue = updateDynamicFormValues(
          dynamicFormValues,
          name,
          searchQuery
        );
        onSelectItem(updatedDynamicValue);
      }
    } else {
      onSelectItem([]);
    }

    navigation.goBack();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 2000);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    if (searchQuery === "") {
      setFilteredData([]);
    }

    if (Array.isArray(options)) {
      const newFilteredData = options?.filter((item) => {
        if (item?.value)
          return item?.value
            ?.toLowerCase()
            ?.includes(searchQuery.toLowerCase());
        else return item?.toLowerCase()?.includes(searchQuery.toLowerCase());
      });
      setFilteredData(newFilteredData);
      if (searchQuery === "") {
        const newData = selectedItems.filter((item) => {
          if (!newFilteredData.includes(item)) {
            if (item?.value)
              return item?.value
                ?.toLowerCase()
                ?.includes(searchQuery.toLowerCase());
            else
              return item?.toLowerCase()?.includes(searchQuery.toLowerCase());
          }
        });
        setFilteredData([...newFilteredData, ...newData]);
      }
    }
  }, [searchQuery, options, selectedItems]);

  const handleItemSelection = (item: any, index: number) => {
    if (selectionType === SelectionType.single) {
      if (selectedItems?.includes(item)) {
        setSelectedItems([]);
      } else {
        setSelectedItems([item]);
      }
    } else if (selectionType === SelectionType.multiple) {
      setSelectedItems((prevSelected) =>
        prevSelected?.includes(item)
          ? prevSelected.filter((prevItem) => prevItem !== item)
          : [...prevSelected, item]
      );
    } else if (selectionType === SelectionType.range) {
      if (startRangeIndex === null) {
        setStartRangeIndex(index);
        setSelectedItems([item]);
      } else {
        const minIndex = Math.min(startRangeIndex, index);
        const maxIndex = Math.max(startRangeIndex, index);
        setSelectedItems(options?.slice(minIndex, maxIndex + 1) as []);
        setStartRangeIndex(null);
      }
    }
  };

  const isDisabled = useCallback(() => {
    return false;
  }, [selectedItems]);

  const renderBottomButtons = () => {
    return (
      <ButtonSection paddingBottom={bottom}>
        <ButtonWrapper>
          <FiltersButton
            isApplyButton={false}
            selectedStyle={selectedStyle}
            disabled={isDisabled()}
            onPress={() => {
              if (!isDisabled()) {
                resetAllFilters();
              }
            }}
          >
            <VDBText
              type={typography["H3-M"]}
              color={
                isDisabled()
                  ? theme["primary-cont-disabled"]
                  : (theme[
                      selectedTheme.secondary as keyof DefaultTheme
                    ] as string)
              }
            >
              {t("searchResult.web.screenOptions.reset.text")}
            </VDBText>
          </FiltersButton>

          <FiltersButton
            onPress={() => {
              if (!isDisabled()) {
                applyFilters();
              }
            }}
            isApplyButton={true}
            selectedStyle={selectedStyle}
            disabled={isDisabled()}
          >
            <VDBText
              type={typography["H3-M"]}
              color={
                isDisabled() ? theme["primary-cont-disabled"] : theme["diam-05"]
              }
            >
              {t("searchResult.web.screenOptions.done.text")}
            </VDBText>
          </FiltersButton>
        </ButtonWrapper>
      </ButtonSection>
    );
  };

  return (
    <>
      <StyledLayoutContainer showAsPopover={theme.isMobile}>
        <View style={{ flex: 1, paddingTop: theme.isMobile ? topOffset : 0 }}>
          <SearchContainer>
            <Controller
              control={control}
              name="searchQueryFormValue"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  inputRef={inputRef}
                  leftIcon={customIcon ? customIcon : ""}
                  rightIcon={VDBIcons.MAIN_SEARCH}
                  inputLabel={t(inputLabel ?? "") || ""}
                  autoFocus={false}
                  onBlur={onBlur}
                  onChangeText={(text) => {
                    onChange(text);
                    setSearchQuery(text);
                  }}
                  value={value}
                />
              )}
            />
            {isFetching && (
              <ActivityContainer>
                <ActivityIndicator
                  color={
                    theme[
                      selectedStyle?.primary as keyof DefaultTheme
                    ] as string
                  }
                />
              </ActivityContainer>
            )}
          </SearchContainer>
          <ListContainer>
            <FlatList
              style={{ flex: 1, minHeight: 0 }}
              keyExtractor={(item: any, index: number) =>
                item?.value?.toString() || item?.toString() || `item-${index}`
              }
              data={
                searchQuery || !server_side_search
                  ? filteredData
                  : selectedItems
              }
              renderItem={({ item, index }) => (
                <CheckBoxDisplay>
                  <RadioButton
                    fillWidth
                    label={item?.label || item}
                    labelColor={
                      selectedItems?.includes(item)
                        ? theme["core-05"]
                        : theme["option-cont"]
                    }
                    checked={
                      selectedItems?.includes(item?.description) ||
                      selectedItems?.includes(item)
                    }
                    styles={typography["Body-R"]}
                    onPress={() => handleItemSelection(item, index)}
                    description={item?.description}
                  />
                </CheckBoxDisplay>
              )}
              contentContainerStyle={{
                paddingLeft: SpaceType.Double,
                paddingRight: SpaceType.Double,
                paddingTop: SpaceType.Basic,
                paddingBottom: SpaceType.Double,
                flexGrow: 1,
              }}
              ItemSeparatorComponent={() => <Separator />}
              keyboardShouldPersistTaps="handled"
            />
          </ListContainer>
          {theme.isLargeScreen ? (
            <Portal hostName={"searchFilter"}>{renderBottomButtons()}</Portal>
          ) : (
            renderBottomButtons()
          )}
        </View>
      </StyledLayoutContainer>
    </>
  );
};

const StyledLayoutContainer = styled(LayoutContainer)`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
  flex-direction: column;
  flex: 1;
`;

const ButtonSection = styled.View<{ paddingBottom: number }>`
  flex-direction: row;
  justify-content: center;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
  gap: ${SpaceType.Double}px;
  padding: ${SpaceType.Double}px 0;
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${({
    paddingBottom,
    theme,
  }: {
    paddingBottom: number;
    theme: DefaultTheme;
  }) =>
    ((theme.isLargeScreen || theme.isTablet) && Platform.OS === "android"
      ? SpaceType.large
      : SpaceType.Double) + paddingBottom}px;
`;

const FiltersButton = styled.TouchableOpacity<{
  isApplyButton: boolean;
  selectedStyle: SelectedItemTheme;
  disabled: boolean;
}>`
  width: 100%;
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 40px;
  border-radius: ${SpaceType.Basic}px;
  background-color: ${({
    theme,
    isApplyButton,
    selectedStyle,
    disabled,
  }: {
    theme: DefaultTheme;
    isApplyButton: boolean;
    selectedStyle: SelectedItemTheme;
    disabled: boolean;
  }) =>
    disabled
      ? theme["primary-bg-disabled"]
      : isApplyButton
      ? (theme[selectedStyle?.one as keyof DefaultTheme] as string)
      : (theme[selectedStyle?.three as keyof DefaultTheme] as string)};
`;

const ButtonWrapper = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Double};
  width: 100%;
  max-width: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? 1072 : 705}px;
  align-items: center;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

const CheckBoxDisplay = styled.View`
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  margin-top: ${SpaceType.Double}px;
  margin-bottom: ${SpaceType.Double}px;
  gap: ${SpaceType.Basic}px;
`;

const Separator = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  width: 100%;
`;

const ActivityContainer = styled.View`
  justify-content: center;
  align-items: center;
  top: 20px;
`;

const SearchContainer = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  margin-top: ${SpaceType.Double}px;
  width: 100%;
  max-width: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? 1072 : 705}px;
  margin-left: auto;
  margin-right: auto;
`;

const ListContainer = styled.View`
  width: 100%;
  max-width: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isLargeScreen ? 1072 : 705}px;
  margin-left: auto;
  margin-right: auto;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

export default DropDownSearchScreen;
